import { decamelize, deSnake } from '../utils/helpers';

export const parseRules = rules => {
  if (!rules) {
    return null;
  }

  const list = rules.split('|');

  return list.map(o => {
    const vals = o.split(':');

    if (vals.length === 1) {
      return vals[0];
    } else if (vals.length >= 2) {
      const rule = {};

      // input is an array of values
      if (/\[/.test(vals[1])) {
        const input = vals[1]
          .replace('[', '')
          .replace(']', '')
          .split(',');

        rule[vals[0]] = input;

        return rule;
      }

      rule[vals[0]] = vals[1];

      return rule;
    }

    return o;
  });
};

export const normalize = (val = null) => {
  if (val !== null && val.length > 0) {
    return val.trim();
  }

  return val;
};

export const isNumber = (val = null) => {
  return /^[0-9]+$/.test(normalize(val));
};

export const isEmpty = (val = null) => {
  if (val !== null && val.length > 0) {
    val = val.trim();
  }

  return val === null || val.length === 0;
};

export const isMinLength = (val = null, length = 2) => {
  if (val === null) {
    return false;
  }

  return val.length >= length;
};

export const isMaxLength = (val = null, length) => {
  if (val === null) {
    return false;
  }

  return val.length <= length;
};

export const isEmail = (val = null) => {
  return /^[a-z][a-z\-_.\d]*@[a-z\-_.\d]*\.[a-z]{2,6}$/i.test(val);
};

export const isBoolean = bool => {
  return typeof bool === 'boolean';
};

export const isInList = (val = null, list = []) => {
  if (!Array.isArray(list)) {
    return;
  }

  // if val is an array
  if (Array.isArray(val)) {
    const checkVals = val.map(o => {
      return list.find(n => {
        return o === n;
      });
    });

    return checkVals.every(o => {
      return !!o;
    });
  }

  if (
    list.find(o => {
      return o === val;
    })
  ) {
    return true;
  }
};

export const hasErrors = (errors = {}) => {
  return Object.keys(errors).length;
};

export const isMinValue = (val = null, min = 0) => {
  return parseInt(val, 10) >= min;
};

export const isMaxValue = (val = null, max) => {
  return parseInt(val, 10) <= max;
};

export const isAlpha = (val = null) => {
  if (!val) {
    return;
  }

  return /^[a-zA-Z]*$/.test(val);
};

export const isConfirmed = (val, confirmation) => {
  return val === confirmation;
};

export const isPassword = (val = null) => {
  return /\S+/.test(val);
};

export const hasDuplicates = (arr = []) => {
  if (arr.length <= 1) {
    return false;
  }

  const dup = arr.filter(val => {
    return (
      arr.filter(o => {
        return o === val;
      }).length > 1
    );
  });

  if (dup.length) {
    return true;
  }

  return false;
};

export const isAlphaDash = () => { };

export const isAlphaNumeric = (val = null) => {
  if (!val) {
    return;
  }

  return /^[a-zA-Z0-9\s-.]*$/.test(val);
};

export const isAlphaSpaces = (val = null) => {
  if (!val) {
    return;
  }

  return /^[a-zA-Z0-9\s-]*$/.test(val);
};

export const isBetween = () => { };

export const isDate = () => { };

export const isBefore = () => { };

export const isAfter = () => { };

export const isDateBetween = () => { };

export const isDecimal = () => { };

export const isCurrency = () => { };

export const isValue = () => { };

export const isNotValue = () => { };

export const isLength = () => { };

export const isArrayLength = () => { };

export const isNotInList = () => { };

export const isInteger = () => { };

export const isRequiredWith = () => { };

export const isUrl = () => { };

const readableField = (field) => {
  return deSnake(decamelize(field));
}

const rules = {
  isNumber(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isNumber(value)) {
      return `${readableField(field)} must be a number`;
    }
  },
  isMinLength(value, field, length) {
    if (isEmpty(value)) {
      return;
    }

    if (!isMinLength(value, length)) {
      return `${readableField(field)} must be at least ${length} characters`;
    }
  },
  isMaxLength(value, field, length) {
    if (isEmpty(value)) {
      return;
    }

    if (!isMaxLength(value, length)) {
      return `${readableField(field)} cannot be more than ${length} characters`;
    }
  },
  isEmail(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isEmail(value)) {
      return `${readableField(field)} must be a valid email`;
    }
  },
  isBoolean(value, field) {
    if (!isBoolean(value)) {
      return `${readableField(field)} should be true or false`;
    }
  },
  isInList(value, field, list = []) {
    if (isEmpty(value)) {
      return;
    }

    if (!isInList(value, list)) {
      const items = list.join(', ');

      return `${readableField(field)} should be in list (${items})`;
    }
  },
  isMinValue(value, field, min) {
    if (isEmpty(value)) {
      return;
    }

    if (!isMinValue(value, min)) {
      return `${readableField(field)} cannot be less than ${min}`;
    }
  },
  isMaxValue(value, field, max) {
    if (isEmpty(value)) {
      return;
    }

    if (!isMaxValue(value, max)) {
      return `${readableField(field)} cannot be greater than ${max}`;
    }
  },
  isAlpha(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isAlpha(value)) {
      return `${readableField(field)} must only contain alpha characters`;
    }
  },
  isAlphaSpaces(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isAlphaSpaces(value)) {
      return `${readableField(
        field
      )} must only contain alpha characters, numbers and spaces`;
    }
  },
  isAlphaNumeric(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isAlphaNumeric(value)) {
      return `${readableField(
        field
      )} must only contain alpha characters and numbers`;
    }
  },
  isConfirmed(value, field, rules) {
    const [compare, compField] = rules.split(',');

    if (isEmpty(value)) {
      return;
    }

    if (!isConfirmed(value, compare)) {
      return `${readableField(field)} should match ${compField}`;
    }
  },
  isPassword(value, field) {
    if (isEmpty(value)) {
      return;
    }

    if (!isPassword(value)) {
      return `${readableField(field)} must be a valid password`;
    }
  },
  required(value, field) {
    // console.log(value);
    if (isEmpty(value)) {
      return `${readableField(field)} is required`;
    }
  }
};

export const validate = (fields, values) => {
  const results = {};

  function NotFoundException(message) {
    this.message = message;
    this.name = 'NotFoundException';
  }

  function checkRule(ruleName, field, value, args) {
    const rule = rules[ruleName];

    if (rule && typeof rule === 'function') {
      const result = rule(value, field.name, args);

      return result;
    } else if (!ruleName) {
      throw new NotFoundException('You must provide a validation rule');
    } else {
      throw new NotFoundException(`No rule ${ruleName}`);
    }
  }

  function getResults(rulesList, field) {
    return rulesList.map(rule => {
      const ruleName = typeof rule === 'object' ? Object.keys(rule)[0] : rule;

      try {
        return checkRule(ruleName, field, values[field.name], rule[ruleName]);
      } catch (e) {
        /* eslint-disable */
        console.error(e); // pass exception object to err handler
        /* eslint-enable */
      }
    });
  }

  fields
    .map(field => {
      const rulesList = parseRules(field.rules);
      let results = [];

      if (!rulesList) {
        return;
      }

      results = getResults(rulesList, field);

      if (results) {
        return {
          [field.name]: results.filter(result => result !== undefined)
        };
      }
    })
    .forEach(result => {
      if (result) {
        const key = Object.keys(result)[0];

        if (result[key].length) {
          results[key] = result[key];
        }
      }
    });

  return results;
};

export default {
  normalize,
  parseRules,
  hasErrors,
  isNumber,
  isEmpty,
  isMinLength,
  isMaxLength,
  isEmail,
  isBoolean,
  isInList,
  isMinValue,
  isMaxValue,
  isAlpha,
  isConfirmed,
  isPassword,
  hasDuplicates,
  validate
};
